import React, { useEffect, useMemo, useState } from "react";
import { Table } from "antd";
import { ExclamationCircleOutlined, SearchOutlined } from "@ant-design/icons";
import "./datatable.css";
import { toSearchText } from "../../../feature-module/utils/filterByColumnSearch";

interface DatatableProps {
  columns: any[];
  loading?: boolean;
  dataSource: any[];
  Selection?: boolean | undefined;
  totalEmployees?: number | undefined;
  handleTableChange?: (pagination: any) => void;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
  };
  rowKey?: string;
  rowSelection?: any;
  onSelectionChange?: (selectedRows: any[]) => void;
  isBordered?: boolean;
  hideSizeChanger?: boolean;
  onRow?: (record: any) => React.HTMLAttributes<any>;
  fixedHeader?: boolean;
  tableHeight?: number | string;
  scroll?: { x?: number | string; y?: number | string };
}

const Datatable: React.FC<DatatableProps> = ({
  columns,
  dataSource,
  Selection,
  totalEmployees,
  handleTableChange,
  pagination,
  onSelectionChange,
  isBordered = false,
  hideSizeChanger = false,
  rowKey = "",
  onRow,
  fixedHeader = false,
  tableHeight = "calc(100vh - 220px)",
  scroll,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [Selections, setSelections] = useState<any>(true);
  const [currentPage, setCurrentPage] = useState<number>(
    pagination?.current ?? 1,
  );
  const [currentSize, setCurrentSize] = useState<number>(
    pagination?.pageSize ?? 1000,
  );

  useEffect(() => {
    if (pagination?.current) setCurrentPage(pagination.current);
  }, [pagination?.current]);

  useEffect(() => {
    if (pagination?.pageSize) setCurrentSize(pagination.pageSize);
  }, [pagination?.pageSize]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: any, selectedRows: any) => {
      setSelectedRowKeys(newSelectedRowKeys);
      onSelectionChange?.(selectedRows);
    },
  };

  useEffect(() => {
    setSelections(Selection);
  }, [Selection]);

  const handleSearch = (value: string) => setSearchText(value);

  const filteredDataSource = useMemo(() => {
    const rows = dataSource ?? [];
    const term = searchText.trim().toLowerCase();

    if (!term) return rows;

    return rows.filter((record) =>
      Object.entries(record).some(([key, value]) =>
        toSearchText(value, key).toLowerCase().includes(term),
      ),
    );
  }, [dataSource, searchText]);

  const onTableChange = (tablePagination: any) => {
    setCurrentPage(tablePagination.current);
    setCurrentSize(tablePagination.pageSize);
    handleTableChange?.(tablePagination);
  };

  const EmptyText = () => (
    <div className="datatable-empty-state">
      <ExclamationCircleOutlined className="datatable-empty-icon" />
      <p className="datatable-empty-title mb-1">No Data Found</p>
    </div>
  );

  const sharedPaginationConfig = {
    current: currentPage,
    pageSize: currentSize,
    locale: { items_per_page: "" },
    showSizeChanger: !hideSizeChanger,
    pageSizeOptions: ["10", "100", "1000", "2000", "3000"],
    showTotal: (total: number, range: [number, number]) =>
      `Showing ${range[0]} - ${range[1]} of ${total} entries`,
    nextIcon: <i className="ti ti-chevron-right" />,
    prevIcon: <i className="ti ti-chevron-left" />,
  };

  const wrapperStyle = fixedHeader
    ? ({
        "--dt-table-height":
          typeof tableHeight === "number" ? `${tableHeight}px` : tableHeight,
      } as React.CSSProperties)
    : undefined;

  return (
    <div className="card datatable-card border-0">
      <div className="card-body p-0">
        {!hideSizeChanger && (
          <div className="datatable-toolbar d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 px-sm-4 py-3">
            <div className="datatable-toolbar-left">
              <div id="DataTables_Table_0_length"></div>
            </div>

            <div className="datatable-toolbar-right ms-auto">
              <div className="datatable-search-wrapper">
                <SearchOutlined className="datatable-search-icon" />
                <input
                  type="search"
                  className="form-control form-control-sm datatable-search-input"
                  placeholder="Search"
                  aria-controls="DataTables_Table_0"
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        <div
          className={`datatable-table-wrapper${fixedHeader ? " datatable-fixed-header" : ""}`}
          style={wrapperStyle}
        >
          {!Selections ? (
            <Table
              bordered={isBordered}
              className="table datanew dataTable no-footer datatable-modern"
              columns={columns}
              rowKey={rowKey}
              rowHoverable={false}
              dataSource={filteredDataSource}
              locale={{ emptyText: <EmptyText /> }}
              pagination={{
                ...sharedPaginationConfig,
                total: pagination?.total ?? filteredDataSource.length,
              }}
              onChange={onTableChange}
              onRow={onRow}
            />
          ) : (
            <Table
              scroll={scroll}
              bordered={isBordered}
              className="table datanew dataTable no-footer datatable-modern"
              rowKey={rowKey}
              rowSelection={rowSelection}
              columns={columns}
              rowHoverable={false}
              dataSource={filteredDataSource}
              onRow={onRow}
              onChange={onTableChange}
              locale={{ emptyText: <EmptyText /> }}
              pagination={{
                ...sharedPaginationConfig,
                total:
                  totalEmployees ??
                  pagination?.total ??
                  filteredDataSource.length,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};
export default Datatable;
